// Single source of truth for the NAMES of URL query params that arrive from
// OUTSIDE the app — emails, push notifications, campaigns, share links, and
// payment/SAT callbacks. Values are the literal query keys.
//
// Page-local transient params (`?filter`, `?tab`, `?year`, `?step`, …) are
// intentionally NOT here — only cross-surface deeplink params. See
// docs/deeplinks.md for the route-vs-query criteria and the per-hook map.
export const DEEPLINK_PARAMS = {
	// ── checkout / cart prefill ──────────────────────────────────────
	ADD_TO_CART: 'add_to_cart',
	COUPON: 'coupon',
	CHECKOUT: 'checkout',
	ADD_PRODUCT: 'add_product',
	// Opens a product's SURFACE by slug (card/asat textarea → checkout, or
	// delegates regularization to its picker). Distinct from ADD_PRODUCT/ADD_TO_CART
	// (which add a pv to the cart). See docs/deeplinks.md.
	PRODUCT: 'product',

	// ── flow router (campaigns) ──────────────────────────────────────
	FLOW: 'flow',

	// ── declarations ─────────────────────────────────────────────────
	PERIOD: 'period',
	PLAN_PERIODICITY: 'periodicity',
	DECLARATION_PERIOD: 'declaration_period',
	DECLARATION_PRESENTED_RATING: 'declaration_presented_rating',
	REGUZ: 'reguz',
	/** Abre el paquete "Ponte al día". Su valor puede ser el brazo del
	 * experimento (`?reg_total=paquete`) para los links de campaña, o `1`
	 * para abrirlo con el brazo que le toque al usuario. */
	REG_TOTAL: 'reg_total',
	/** `?ponte_al_dia=<arm>` rewrites the user's frozen arm in the back and
	 * reloads. Only for internal and QA accounts: the back rejects any other. */
	PONTE_AL_DIA_OVERRIDE: 'ponte_al_dia',
	/**
	 * `?preview_purchase=<caso>` pinta la pantalla de éxito con una compra
	 * inventada, para diseñarla sin pagar. Solo hace algo con
	 * `VITE_PURCHASE_PREVIEW=true`; sin esa env el param se ignora.
	 */
	PURCHASE_PREVIEW: 'preview_purchase',
	MODAL: 'modal',

	// ── platform / attribution ───────────────────────────────────────
	PLATFORM_OS: 'platform_os',
	VIEWED_FROM: 'viewed_from',
	/** Local development only: `?app_update=optional|required` forces the app update prompt for this page load. */
	APP_UPDATE_FORCE: 'app_update',
	CHANNEL_SOURCE: 'channel_source',

	// ── auth ─────────────────────────────────────────────────────────
	TOKEN: 'token',
	CLEAR_STORAGE: 'clear_storage',
	ONBOARDING_TARGET: 'onboarding_target',
	// `?partner=<slug>` — la URL que reparte un aliado en sus campañas. Se
	// resuelve contra el catálogo de aliados y se guarda como el mismo target
	// que ONBOARDING_TARGET, así que sobrevive el signup completo.
	PARTNER: 'partner',
	// `?calc=<calculadora>` — el segmento que el anuncio del aliado YA conoce.
	// Solo lo entienden los aliados que declaran `campaignRole` en el catálogo
	// (Calcu.mx: la calculadora que el visitante acaba de usar dice su régimen).
	// Viaja pegado a PARTNER y se consume con él, en el mismo target guardado.
	CALC: 'calc',
	// `?ref=<id>` — el identificador PROPIO del aliado para su visitante. Viaja
	// pegado a PARTNER, se guarda con el target y el host lo estampa en el record
	// de onboarding (`answers.partner_ref`), para que el aliado cruce el reporte
	// por API con su base sin que le pasemos datos personales.
	PARTNER_REF: 'ref',
	// `?offer=annual` — el link del aliado vende la declaración anual en vez del
	// plan. Solo lo entienden los aliados con `annualOffer` en el catálogo
	// (Maslow). Viaja pegado a PARTNER; NO agrega nada al carrito: el usuario
	// compra la anual desde su tarjeta en la pantalla del plan.
	PARTNER_OFFER: 'offer',
	// `?intent=<slug>`: what the visitor came for (`facturar`). Stored in its
	// own slot and stamped as `answers.entry_intent`, never as
	// ONBOARDING_TARGET: that one is the ally attribution. Only read while the
	// `onboarding-solo-facturar` flag is on; next to PARTNER, the ally wins.
	INTENT: 'intent',

	// ── notifications ────────────────────────────────────────────────
	PUSH_NOTIFICATION_ID: 'push_notification_id',

	// ── nps ──────────────────────────────────────────────────────────
	// `?nps=1` abre el modal de NPS (WhatsApp/push del journey, t-015). No
	// depende del rollout de `nps_modal`; se corta con `nps_deeplink_disabled`.
	// No se abre si ya respondió en la ventana (backend).
	NPS: 'nps',

	// ── fiscal info ──────────────────────────────────────────────────
	// `?fiscal_info=1` abre el drawer "Tu información fiscal" (o, sin datos
	// fiscales, la vinculación del SAT) en cualquier ruta autenticada.
	FISCAL_INFO: 'fiscal_info',

	// ── certificates campaign landing ────────────────────────────────
	// `/certificates/?src=<origen>&c=<campaña>` (t-152): el enlace que reciben
	// los Rappitenderos. Guarda el destino para que login y onboarding lo
	// devuelvan a Certificados al terminar. `c` es el nombre del lote.
	SRC: 'src',
	CAMPAIGN: 'c',

	// ── support chat ─────────────────────────────────────────────────
	// `/support?conversationId=<uuid>` abre esa conversación de Ágora (la de un
	// caso del CRM) en vez del chat general. Lo trae el push de una respuesta.
	// `=general` abre el chat general aunque haya casos abiertos (sin el param,
	// con casos abiertos, `/support` muestra la lista de conversaciones).
	CONVERSATION_ID: 'conversationId',
	// `&from=list` next to CONVERSATION_ID: the conversation was opened from the
	// list, so it shows a back arrow to it. It lives in the URL, not in router
	// state, because global hooks replace the URL and wipe `history.state`.
	CONVERSATION_FROM: 'from',

	// ── articles ─────────────────────────────────────────────────────
	ARTICLE: 'article',
	ARTICLE_SLUG: 'article_slug',

	// ── referrals ────────────────────────────────────────────────────
	REFERRAL_CODE: 'referral_code',
	CAMPAIGN_ID: 'campaign_id',
	OPEN_REFERRALS: 'open_referrals',

	/** Clic de una red de afiliados montada sobre Affise. Los nombres los pone
	 * Affise, no nosotros: `sub1` es el click id y `sub2` el publisher id. */
	AFFILIATE_CLICK_ID: 'sub1',
	AFFILIATE_PID: 'sub2',

	// ── plans page sections (deeplinkable) ───────────────────────────
	PREBILLING: 'prebilling',
	PLAN_CATEGORY: 'plan_category',

	// ── SAT linking callback ─────────────────────────────────────────
	LINKING_STEP: 'linking_step',
	LINKING_METHOD: 'linking_method',

	// ── Stripe callback ──────────────────────────────────────────────
	SHOW_MODAL_SUCCESSFUL: 'show_modal_successful',
	REDIRECT_STATUS: 'redirect_status',

} as const;

export type DeeplinkParam =
	(typeof DEEPLINK_PARAMS)[keyof typeof DEEPLINK_PARAMS];

// Per-product dynamic suffixes for the cart-prefill deeplink. NOT enumerable
// in DEEPLINK_PARAMS — the suffix is a product_version_id known only at
// runtime. `?year_<pv>=<int>` (annual) / `?asat_<pv>=<text>` (asat message).
// PRODUCT_EXTRA_PARAM_RE matches both so the parser can strip them in bulk.
export const productExtraParam = {
	year: (productVersionId: number) => `year_${productVersionId}`,
	asat: (productVersionId: number) => `asat_${productVersionId}`,
};
export const PRODUCT_EXTRA_PARAM_RE = /^(?:year|asat)_\d+$/;

// `?flow=<value>` is overloaded across unrelated consumers. The value-spaces
// are disjoint except `failed_renewal`, which is BOTH a router key and the
// banner trigger:
//   router keys (useFlowRedirect): subscription | reg | annual | asat |
//                                approval | lucy | failed_renewal
//   auth mode   (auth/index):    signup
//   banner      (FailedRenewalBanner): failed_renewal
export const FLOW_VALUES = {
	SIGNUP: 'signup',
	FAILED_RENEWAL: 'failed_renewal',
} as const;
